/**
 * Bewegung des Heroes (Masterprompt Abschnitte 28, 29, 43).
 *
 * Diese Datei ist der Kern der serverautoritativen Bewegung - und sie wird von
 * BEIDEN Seiten benutzt:
 *
 * - Der Server rechnet damit die verbindliche Position aus.
 * - Der Client rechnet mit EXAKT derselben Funktion voraus, wohin er sich
 *   vermutlich bewegen wird, damit die Steuerung sich verzoegerungsfrei anfuehlt.
 *
 * Genau deshalb steht sie hier und nicht in einem der beiden Programme: Es darf
 * nur eine einzige Definition von "so bewegt sich ein Hero" geben. Weicht die
 * Vorhersage vom Server ab, korrigiert der Server - und behaelt immer recht.
 */

import type { PlayerSide } from '@dueline/shared';
import { clampToSideBounds, getAttackDirection } from './shared-lane.js';
import { HERO_LEASH_BRAKE_ZONE } from '@dueline/game-data';
import { bremsFaktor } from './leash.js';
import { blockierteBewegung, type Hindernis } from './unit-separation.js';
import {
  haltImRaum,
  istGeschuetzt,
  istImRaum,
  type SanctuaryZustand,
} from './sanctuary.js';

/** Was die Bewegung veraendern darf. Passt sowohl auf den Serverzustand als auch
 *  auf die Vorhersage im Client. */
export interface MovableHero {
  x: number;
  y: number;
  facing: number;
}

/** Die Absicht des Spielers: eine Richtung, keine Position. */
export interface MoveCommand {
  moveX: number;
  moveY: number;
}

/**
 * Darf dieser Hero gerade ueberhaupt handeln?
 *
 * Drei Gruende sprechen dagegen, und alle drei gelten fuer Bewegung UND
 * Angriff:
 *
 *   tot          - nichts geht,
 *   am Wirken    - ein Barrage haelt ihn fest,
 *   betaeubt     - seit Hero gegen Hero (GAME_DESIGN Abschnitt 5b),
 *   im Sanctuary - er ist aus dem Kampf genommen (Abschnitt 5f).
 *
 * Die Regel steht hier und nicht zweimal im Code: Der Server entscheidet
 * damit, ob er eine Eingabe annimmt, der Client, ob er sie ueberhaupt
 * schickt und vorhersagt. Haette nur einer von beiden die Betaeubung
 * gekannt, liefe die Figur im Bild los, waehrend sie auf dem Server steht.
 */
/**
 * Soll die Eingabe ueberhaupt zum Server geschickt werden?
 *
 * Fast dasselbe wie heroDarfHandeln - mit EINEM Unterschied: Das
 * Sanctuary zaehlt hier nicht. Drinnen bewegt sich der Hero zwar nicht,
 * aber genau diese Eingabe ist das Signal "ich will hinaus"
 * (GAME_DESIGN Abschnitt 5f).
 *
 * Im Spiel aufgefallen am 09.10.2026: Mit nur einer Regel fuer beide
 * Fragen kam niemand mehr aus dem Sanctuary heraus - weder ueber den
 * Client noch ueber den Raum.
 */
export function heroSendetEingabe(zustand: {
  alive: boolean;
  channelSeconds: number;
  stunnedFor: number;
}): boolean {
  return zustand.alive && zustand.channelSeconds <= 0 && zustand.stunnedFor <= 0;
}

export function heroDarfHandeln(zustand: {
  alive: boolean;
  channelSeconds: number;
  stunnedFor: number;
  /** Zustand im Sanctuary-Ablauf. Fehlt er, zaehlt "draussen". */
  sanctuary?: string;
}): boolean {
  if (istGeschuetzt((zustand.sanctuary ?? 'draussen') as SanctuaryZustand)) return false;
  return zustand.alive && zustand.channelSeconds <= 0 && zustand.stunnedFor <= 0;
}

/**
 * Darf dieser Hero sich gerade BEWEGEN?
 *
 * Fast dasselbe wie heroDarfHandeln, mit einem Unterschied: Das Sanctuary
 * haelt hier niemanden fest. Im Raum MUSS er laufen duerfen, sonst kaeme er
 * nicht zum Tor zurueck (09.10.2026) - und seit dem 12.10.2026 auch im Tor
 * selbst, denn Weggehen bricht den Rueckweg ab.
 *
 * Angreifen und Zaubern bleiben dort gesperrt; dafuer gilt weiter
 * heroDarfHandeln. Zwei Fragen, zwei Antworten - eine gemeinsame waere
 * genau der Fehler, der am 09.10.2026 schon einmal dazu gefuehrt hat,
 * dass niemand mehr aus dem Sanctuary herauskam.
 */
export function heroDarfLaufen(zustand: {
  alive: boolean;
  channelSeconds: number;
  stunnedFor: number;
  sanctuary?: string;
}): boolean {
  return zustand.alive && zustand.channelSeconds <= 0 && zustand.stunnedFor <= 0;
}

/**
 * Bringt eine Eingaberichtung auf eine gueltige Laenge zwischen 0 und 1.
 *
 * Zwei Dinge passieren hier:
 *
 * 1. **Diagonale wird nicht schneller.** Wer gleichzeitig nach vorne und zur
 *    Seite drueckt, hat sonst eine Richtung der Laenge 1,41 - und waere damit
 *    41 Prozent schneller unterwegs als geradeaus.
 * 2. **Schutz vor Manipulation.** Ein veraenderter Client koennte eine Richtung
 *    der Laenge 1000 schicken. Sie wird hier auf 1 gekuerzt, bevor irgendetwas
 *    damit gerechnet wird.
 *
 * Ungueltige Zahlen (NaN, Unendlich) werden als "keine Eingabe" behandelt.
 */
export function normalizeMoveCommand(command: MoveCommand): MoveCommand {
  const moveX = Number.isFinite(command.moveX) ? command.moveX : 0;
  const moveY = Number.isFinite(command.moveY) ? command.moveY : 0;

  const length = Math.hypot(moveX, moveY);
  if (length <= 0) return { moveX: 0, moveY: 0 };
  if (length <= 1) return { moveX, moveY };

  return { moveX: moveX / length, moveY: moveY / length };
}

/**
 * Wendet einen Bewegungsschritt an. Veraendert den uebergebenen Hero direkt.
 *
 * @param hero    Wird veraendert: x, y und facing.
 * @param command Richtung, in die der Spieler laufen moechte.
 * @param speed   Bewegung in Welteinheiten pro Sekunde.
 * @param dt      Dauer dieses Simulationsschritts in Sekunden.
 * @param side    Auf welcher Seite der gemeinsamen Lane er zuhause ist.
 *                Seit dem 08.10.2026 noetig: Die Bewegungsgrenze haengt
 *                an der Seite, nicht mehr an der Karte allein.
 * @param leashX  Der am weitesten vorn liegende x-Wert, den er erreichen
 *                darf (siehe leash.ts). Pflichtangabe und nicht optional:
 *                Wer sie vergisst, soll es vom Uebersetzer erfahren und
 *                nicht erst daran, dass ein Hero durch die halbe
 *                gegnerische Haelfte laeuft.
 * @param sanctuary Zustand im Sanctuary-Ablauf. Im Raum gelten andere
 *                Grenzen und keine Leash - er ist ja nicht mehr auf der
 *                Lane (Produktentscheidung vom 09.10.2026).
 */
export function applyHeroMovement(
  hero: MovableHero,
  command: MoveCommand,
  speed: number,
  dt: number,
  side: PlayerSide,
  leashX: number,
  sanctuary: SanctuaryZustand = 'draussen',
  /**
   * Was ihm koerperlich im Weg steht (Produktentscheidung vom 13.10.2026).
   *
   * Zuerst wurde der Hero im Gedraenge nur LANGSAMER. Robin im Spiel: "das
   * passt doch nicht gut, mach eher, dass er auch hart blockiert wird."
   * Jetzt gilt fuer ihn dieselbe Regel wie fuer Creeps - er kommt nicht
   * hindurch, rutscht aber an der Reihe entlang.
   *
   * Die Hindernisse kommen von aussen und werden hier nicht gesucht:
   * Server und Client muessen dieselben benutzen, sonst laeuft die
   * Vorhersage an jeder Creepreihe auseinander.
   */
  kollision?: {
    hindernisse: readonly Hindernis[];
    mindestabstand: number;
    /** Wie weit er je Takt aus einem anderen herausrutschen darf. */
    entflechtung: number;
  },
): void {
  const { moveX, moveY } = normalizeMoveCommand(command);

  if (moveX === 0 && moveY === 0) return;

  const safeSpeed = Number.isFinite(speed) && speed > 0 ? speed : 0;
  const safeDt = Number.isFinite(dt) && dt > 0 ? dt : 0;
  const distance = safeSpeed * safeDt;

  /*
   * Im Raum neben der Lane gelten dessen Waende - und keine Leash.
   *
   * Ohne diesen Zweig zoege clampToSideBounds den Hero im selben Takt
   * auf die Lane zurueck, in dem er versetzt wurde: Der Raum liegt
   * ausserhalb der Lane-Grenzen.
   */
  if (istImRaum(sanctuary)) {
    const ziel = haltImRaum({ x: hero.x + moveX * distance, y: hero.y + moveY * distance }, side);
    hero.x = ziel.x;
    hero.y = ziel.y;
    hero.facing = Math.atan2(moveY, moveX);
    return;
  }

  /*
   * Die Leash bremst NUR nach vorne (GAME_DESIGN Abschnitt 5a).
   *
   * Zur Seite und nach hinten bleibt der Hero immer voll schnell - wer
   * sich zurueckziehen will, darf das sofort. Gebremst wird allein der
   * Anteil der Bewegung, der ihn weiter nach vorne traegt.
   */
  const vorwaerts = getAttackDirection(side === 'blue' ? 'orange' : 'blue');
  let schrittX = moveX * distance;

  const nachVorne = schrittX * vorwaerts;
  if (nachVorne > 0) {
    /*
     * Begrenzt wird der SCHRITT, nicht die Position.
     *
     * Ein erster Entwurf klemmte die Zielposition auf die Grenze. Stand
     * der Hero schon davor - etwa weil seine Armee gefallen und die Linie
     * zurueckgesprungen war -, riss ihn das sofort nach hinten. Die Leash
     * soll aufhalten, nicht ziehen.
     */
    /*
     * Die Kante ist weich (Rueckmeldung vom 12.10.2026).
     *
     * Wirklich Schluss ist erst eine Bremszone HINTER der Linie. Der Hero
     * laeuft also in den Rauch hinein, wird dabei immer langsamer und
     * bleibt dort stehen - statt an der Linie gegen eine Wand zu laufen.
     */
    const schluss = leashX + vorwaerts * HERO_LEASH_BRAKE_ZONE;
    const rest = (schluss - hero.x) * vorwaerts;
    const gebremst = Math.min(nachVorne * bremsFaktor(hero.x, schluss, vorwaerts), rest);
    schrittX = Math.max(0, gebremst) * vorwaerts;
  }

  const zielX = hero.x + schrittX;

  const gewuenscht = clampToSideBounds(
    {
      x: zielX,
      y: hero.y + moveY * distance,
    },
    side,
  );

  /*
   * Und jetzt die Koerper (13.10.2026).
   *
   * Erst die Leash, dann die Kartengrenze, dann die Kollision: Die Leash
   * sagt, wie weit er DARF, die Kollision, wo gerade jemand STEHT. Danach
   * noch einmal in die Grenzen geklemmt - ein Creep am Lane-Rand soll ihn
   * nicht aus dem Feld druecken.
   */
  const target = kollision
    ? clampToSideBounds(
        blockierteBewegung({
          von: { x: hero.x, y: hero.y },
          nach: gewuenscht,
          hindernisse: kollision.hindernisse,
          mindestabstand: kollision.mindestabstand,
          entflechtung: kollision.entflechtung,
        }),
        side,
      )
    : gewuenscht;

  hero.x = target.x;
  hero.y = target.y;
  hero.facing = Math.atan2(moveY, moveX);
}
